/**
 * `useFocus(config, search)`: everything a screen needs from the Focus —
 * account availability, Focus resolution, data bounds, the resolved scope,
 * the navigations that change it, and the "your Focus was replaced" notice.
 *
 * The URL is the only source of truth for the scope; this hook reads it,
 * completes it (resolve.ts) and writes changes back to it on the CURRENT
 * route (`to: '.'`), re-parsed with the screen's own schema so the screen's
 * keys survive and every URL is canonical.
 *
 * The Focus is ONE selection across screens: every screen reads the same
 * GET /api/admaster/focus, and changing apps (or the 7/14/28-day window) on
 * any screen saves it with PUT — except in support sessions (read-only) and
 * for All apps (the portfolio is not a Focus).
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  availabilityQuery,
  dateBoundsQuery,
  focusApi,
  focusAppsQuery,
  focusKeys,
  focusQuery,
  type AvailabilityState,
  type DateRangeValues,
  type FocusApp,
  type FocusAppsResponse,
  type FocusResponse,
  type PerformanceAvailability,
} from './api';
import type { FocusConfig } from './config';
import { WINDOW_OF_PERIOD, matchingPeriod, trailingWindow, type DataBounds, type WindowDays } from './dates';
import { needsFocus, resolveSearch, type Resolution } from './resolve';
import { EMPTY_FILTERS, paramsKey, toApiParams, type FocusApiParams, type FocusFilters, type FocusSearch, type Period } from './search';
import { recommendedWindow } from './window';
import { useConnections, useCurrentUser } from '@/auth/useSession';
import { useSampleActive } from '@/lib/sample';
import { readStored, writeStored } from '@/lib/storage';
import type { RawSearch } from '@/router/search';

/** The resolved scope of a screen without dates (e.g. Creative lab). */
export interface FocusAppScope {
  /** Selected apps, `appId::OS` composites; always ≥1 once resolved (every entitled app for All apps). */
  appIds: string[];
  /** Applied (not draft) scope, already encoded for the API. Pass straight to `api.get`. */
  apiParams: FocusApiParams;
  /** Changes whenever the applied scope changes; use in query keys. */
  key: string;
}

/** The resolved scope of a screen with dates. */
export interface FocusScope extends FocusAppScope {
  /** Inclusive UTC dates, YYYY-MM-DD. */
  dateFrom: string;
  dateTo: string;
  /** 7/14/28 when the dates are a data-anchored "Last N days"; null for any other range. */
  windowDays: WindowDays | null;
}

/** `FocusScope` for a config with dates, `FocusAppScope` without. */
export type ScopeOf<C> = C extends { dates: false } ? FocusAppScope : FocusScope;

export interface FocusActions<S extends FocusSearch> {
  /** Apps as a set; `'all'` is the portfolio view (configs with `allApps` only). */
  setApps: (selection: string[] | 'all') => void;
  setDates: (from: string, to: string, period: Period | undefined) => void;
  /** Commit the Filters panel (apps included — the App field is the same selection). */
  applyFilters: (filters: FocusFilters) => void;
  removeFilters: (keys: (keyof FocusFilters)[]) => void;
  /** Replace the whole URL (e.g. a saved report): push, so Back returns to the previous view. */
  replaceScope: (next: S) => void;
}

export interface FocusModel<S extends FocusSearch, Scope extends FocusAppScope = FocusScope> {
  config: FocusConfig<S>;
  search: S;
  availability: UseQueryResult<PerformanceAvailability>;
  /** The account-wide reason there is nothing to show, once known. */
  accountState: AvailabilityState | undefined;
  focus: UseQueryResult<FocusResponse>;
  focusApps: UseQueryResult<FocusAppsResponse>;
  boundsQuery: UseQueryResult<DateRangeValues>;
  /** Data bounds of the scope: undefined while loading, null when they failed. */
  bounds: DataBounds | null | undefined;
  resolution: Resolution<S>;
  /** The URL needs the Focus (it names no app). */
  wantsFocus: boolean;
  /** The resolved scope; while a changed URL resolves, the previous one. */
  scope: Scope | null;
  actions: FocusActions<S>;
  /** "Your saved Focus was replaced…", shown once per browser session. */
  notice: { message: string | null; dismiss: () => void };
}

const REPLACED_SEEN_KEY = 'lemon.focus.replacedSeen';

/** The bounds query ignores dates and status (neither changes the answer). */
const boundsParamsOf = (search: FocusSearch) => toApiParams({ ...search, status: [] }, { dates: false });

/**
 * Changing apps keeps the user's window, but a data-anchored one ("Last 14
 * days") re-anchors on the new apps' latest data day. When every new app's
 * latest day is known the dates are computed at once (one history entry, no
 * reload of the page chrome); otherwise they are dropped and resolve.ts
 * re-resolves them against the new scope's data bounds.
 */
function reanchor<S extends FocusSearch>(next: S, apps: readonly FocusApp[] | undefined): S {
  const days = next.period ? WINDOW_OF_PERIOD[next.period] : undefined;
  if (!days && next.period !== 'lifetime') return next;
  const selected = next.all_apps ? (apps ?? []) : next.app_id.map((id) => apps?.find((a) => a.id === id));
  const latest = selected.map((a) => a?.latest_data_day ?? null);
  if (days && selected.length && latest.every((d): d is string => d !== null)) {
    const range = trailingWindow(days, latest.reduce((a, b) => (a > b ? a : b)));
    return { ...next, date_from: range.from, date_to: range.to };
  }
  return { ...next, date_from: undefined, date_to: undefined };
}

/** Saves the Focus when the user changes apps or the 7/14/28-day window. */
function useSaveFocus() {
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const { mutate } = useMutation({
    mutationFn: focusApi.saveFocus,
    onSuccess: (saved: FocusResponse) => { queryClient.setQueryData(focusKeys.focus(), saved); },
    // A Focus is a remembered preference: failing to save it must not interrupt
    // the user (the URL still shows what they chose). The next change retries.
  });
  const supportSession = Boolean(user.data?.support_session);
  const sample = useSampleActive();
  return useCallback(
    (appIds: string[], windowDays: WindowDays) => {
      // Support sessions and sample data are read-only; the portfolio is not a Focus.
      if (supportSession || sample || !appIds.length) return;
      mutate({ app_ids: appIds.slice(0, 200), window_days: windowDays });
    },
    [mutate, supportSession, sample],
  );
}

/**
 * Whether the account has data of its own to show: no connection at all,
 * else the API's availability check. On sample data (src/lib/sample.ts) the
 * user's own connections do not count; the sample's availability answers.
 */
export function useAccountState() {
  const connections = useConnections();
  const availability = useQuery(availabilityQuery());
  const sample = useSampleActive();
  const noConnections = !sample && connections.isSuccess && connections.data.length === 0;
  const accountState: AvailabilityState | undefined = noConnections ? 'no_connections' : availability.data?.state;
  return { availability, accountState };
}

export function useFocus<S extends FocusSearch, C extends FocusConfig<S>>(config: C, search: S): FocusModel<S, ScopeOf<C>> {
  const navigate = useNavigate();
  const { availability, accountState } = useAccountState();
  const focusApps = useQuery(focusAppsQuery());
  const wantsFocus = needsFocus(search);
  // Fetched in parallel with availability; only APPLIED once the account has data.
  // A screen without dates also reads it to keep the user's window when saving.
  const focus = useQuery({ ...focusQuery(), enabled: wantsFocus || !config.dates });
  const boundsParams = useMemo(() => boundsParamsOf(search), [search]);
  const boundsQuery = useQuery({ ...dateBoundsQuery(boundsParams), enabled: config.dates && !wantsFocus });

  const boundsData = boundsQuery.data;
  const boundsFailed = boundsQuery.isError;
  const bounds = useMemo<DataBounds | null | undefined>(
    () => (boundsData ? { first: boundsData.date_from, last: boundsData.date_to } : boundsFailed ? null : undefined),
    [boundsData, boundsFailed],
  );

  const accountReady = accountState === 'available';

  const resolution: Resolution<S> = accountReady
    ? resolveSearch({ search, dates: config.dates, focus: focus.data, focusApps: focusApps.isError ? [] : focusApps.data?.apps, bounds })
    : { kind: 'wait' };

  const [notice, setNotice] = useState<string | null>(null);

  const { parse, onScopeChange } = config;
  // Re-parsed so every URL is canonical (same key order, same validation as a pasted link).
  const go = useCallback(
    (next: S, replace = false): Promise<void> => navigate({ to: '.', search: parse(next as unknown as RawSearch), replace }),
    [navigate, parse],
  );

  const target = resolution.kind === 'redirect' ? resolution.search : null;
  const targetKey = target ? JSON.stringify(target) : null;
  const replaced = resolution.kind === 'redirect' ? resolution.replaced : null;
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!target || !targetKey || handled.current === targetKey) return;
    handled.current = targetKey;
    // A replaced Focus is explained once (per browser session), quietly, once the URL shows its replacement.
    const message = replaced && readStored(REPLACED_SEEN_KEY, 'session') !== replaced.message ? replaced.message : null;
    if (message) writeStored(REPLACED_SEEN_KEY, message, 'session');
    void go(target, true).then(() => { if (message) setNotice(message); });
  }, [target, targetKey, replaced, go]);

  const resolvedApps = focusApps.data?.apps;
  const scope = useMemo<FocusScope | FocusAppScope | null>(() => {
    if (resolution.kind !== 'ready') return null;
    const apiParams = toApiParams(search);
    const base: FocusAppScope = {
      // Portfolio: every entitled app is in view (the API is not given app_id).
      appIds: search.all_apps ? (resolvedApps?.map((a) => a.id) ?? []) : search.app_id,
      apiParams,
      key: paramsKey(apiParams),
    };
    if (!config.dates) return base;
    if (!search.date_from || !search.date_to) return null;
    const dates = { from: search.date_from, to: search.date_to };
    const period = matchingPeriod(search.period, dates, bounds ?? null);
    return { ...base, dateFrom: dates.from, dateTo: dates.to, windowDays: period ? (WINDOW_OF_PERIOD[period] ?? null) : null };
  }, [resolution.kind, search, bounds, resolvedApps, config.dates]);

  // While a changed URL resolves (e.g. dates re-anchoring), keep showing the
  // previous scope instead of dropping the page back to its skeleton.
  const [lastScope, setLastScope] = useState<FocusScope | FocusAppScope | null>(null);
  if (scope && scope !== lastScope) setLastScope(scope);
  const pending = accountReady && (resolution.kind === 'wait' || resolution.kind === 'redirect');
  const shown = scope ?? (pending ? lastScope : null);

  // ------------------------------------------------------------ actions --

  const saveFocus = useSaveFocus();
  const shownWindow = (shown as Partial<FocusScope> | null)?.windowDays ?? null;
  const focusWindow = focus.data?.window_days ?? null;
  /** The window a Focus save keeps: the one in view, else the saved one, else the automatic one. */
  const windowFor = useCallback(
    (appIds: string[]): WindowDays => shownWindow ?? focusWindow ?? recommendedWindow(appIds, resolvedApps),
    [shownWindow, focusWindow, resolvedApps],
  );
  const changed = useCallback((next: S): S => (onScopeChange ? onScopeChange(next) : next), [onScopeChange]);

  const setApps = useCallback(
    (selection: string[] | 'all') => {
      const portfolio = selection === 'all' && config.allApps;
      const appIds = selection === 'all' ? [] : selection;
      if (!portfolio && !appIds.length) return;
      void go(changed(reanchor({ ...search, app_id: appIds, all_apps: portfolio ? '1' : undefined }, resolvedApps)));
      if (!portfolio) saveFocus(appIds, windowFor(appIds));
    },
    [search, go, changed, saveFocus, windowFor, resolvedApps, config.allApps],
  );

  const setDates = useCallback(
    (from: string, to: string, period: Period | undefined) => {
      void go(changed({ ...search, date_from: from, date_to: to, period }));
      const days = period ? WINDOW_OF_PERIOD[period] : undefined;
      if (days && !search.all_apps) saveFocus(search.app_id, days);
    },
    [search, go, changed, saveFocus],
  );

  const applyFilters = useCallback(
    (filters: FocusFilters) => {
      const appsChanged = filters.all_apps !== search.all_apps || filters.app_id.join('\n') !== search.app_id.join('\n');
      const next: S = { ...search, ...filters };
      void go(changed(appsChanged ? reanchor(next, resolvedApps) : next));
      if (appsChanged && !filters.all_apps) saveFocus(filters.app_id, windowFor(filters.app_id));
    },
    [search, go, changed, saveFocus, windowFor, resolvedApps],
  );

  const removeFilters = useCallback(
    (keys: (keyof FocusFilters)[]) => {
      const next = { ...search } as unknown as Record<string, unknown>;
      for (const key of keys) next[key] = EMPTY_FILTERS[key];
      void go(changed(next as unknown as S));
    },
    [search, go, changed],
  );

  const replaceScope = useCallback((next: S) => { void go(next); }, [go]);

  const actions = useMemo(
    () => ({ setApps, setDates, applyFilters, removeFilters, replaceScope }),
    [setApps, setDates, applyFilters, removeFilters, replaceScope],
  );

  return {
    config,
    search,
    availability,
    accountState,
    focus,
    focusApps,
    boundsQuery,
    bounds,
    resolution,
    wantsFocus,
    scope: shown as ScopeOf<C> | null,
    actions,
    notice: { message: notice, dismiss: () => { setNotice(null); } },
  };
}
